import moment from 'moment'
import lc from 'locale-code'
import store from '@/store/index'
import { http } from '@/api/http'
import { unref } from 'vue'
import { createI18n } from 'vue-i18n'
import { localize } from '@vee-validate/i18n'
import en from '@vee-validate/i18n/dist/locale/en.json'
import de from '@vee-validate/i18n/dist/locale/de.json'

localize({
  en,
  de,
})

const numberFormats = {
  'de-DE': {
    decimal: {
      style: 'decimal',
      minimumFractionDigits: 2,
    },
    percent: {
      style: 'percent',
      minimumFractionDigits: 2,
    },
  },
}

// load vee-validate locale and change ex. de_DE to de-DE
function loadLocale(code) {
  const locale = lc.getLanguageCode(code)
  localize(locale)
}

export const getLang = () => {
  return window.config?.locale || (import.meta.env.VITE_I18N_LOCALE || 'de-DE').split('_')[0]
}

export const getCountry = () => {
  return window.config?.country || (import.meta.env.VITE_I18N_LOCALE || 'de-DE').split('_')[1]
}

export const getLocale = () => {
  return `${getLang()}-${getCountry()}`
}

export const getFallbackLocale = () => {
  const lang = window.config?.locale || (import.meta.env.VITE_I18N_FALLBACK_LOCALE || 'de-DE').split('_')[0]
  const country = window.config?.country || (import.meta.env.VITE_I18N_FALLBACK_LOCALE || 'de-DE').split('_')[1]
  return `${lang}-${country}`
}

export const getFormattedLocale = () => {
  const lang = window.config?.locale || (import.meta.env.VITE_I18N_FORMATED_LOCALE || 'de-DE').split('_')[0]
  const country = window.config?.country || (import.meta.env.VITE_I18N_FORMATED_LOCALE || 'de-DE').split('_')[1]
  return `${lang}_${country}`
}

export const i18n = createI18n({
  numberFormats,
  locale: getLocale(),
  fallbackLocale: getFallbackLocale(),
  formatedLocale: getFormattedLocale(),
})

/**
 * Locale segment for URL paths (e.g. `de-de`). Uses vue-i18n’s active locale when set,
 * otherwise {@link getLocale} from `window.config`. Avoids duplicating `locale-country` manually.
 */
export function getPathLocale() {
  const active = unref(i18n.global.locale)
  if (active != null && active !== '') {
    return String(active).toLowerCase()
  }
  return String(getLocale()).toLowerCase()
}

const getLoadedLanguages = () => {
  return window.loadedLanguages || []
}

const setLoadedLanguages = (languages) => {
  window.loadedLanguages = languages
}

const getStoredTranslation = (locale) => {
  if (!window.translations) {
    return null
  }
  return window.translations[locale] || null
}

const setStoredTranslation = (locale, data) => {
  if (!window.translations) {
    window.translations = {}
  }
  window.translations[locale] = data
}

let loadedLanguages = getLoadedLanguages()

const setI18nLocale = (locale) => {
  i18n.locale = locale
  i18n.formatedLocale = locale.replace(/-/g, '_')
  moment.locale(locale)
  document.querySelector('html').setAttribute('lang', locale)
  return locale
}

export const loadLanguageAsync = () => {
  const locale = getLocale()
  const lang = getLang()
  const country = getCountry()
  store.dispatch('translations/togglePendingTranslations')

  // If the same language or it was already loaded in this instance
  if ((i18n.locale === locale && loadedLanguages.includes(locale)) || loadedLanguages.includes(locale)) {
    loadLocale(locale)
    store.dispatch('translations/togglePendingTranslations')
    return Promise.resolve(setI18nLocale(locale))
  }

  // If the language was already fetched and stored in sessionStorage, reuse it without calling the API again
  const storedTranslation = getStoredTranslation(locale)
  if (storedTranslation) {
    i18n.global.setLocaleMessage(locale, storedTranslation)
    loadedLanguages.push(locale)
    setLoadedLanguages(loadedLanguages)
    loadLocale(locale)
    store.dispatch('translations/togglePendingTranslations')
    return Promise.resolve(setI18nLocale(locale))
  }

  return new Promise(resolve => {
    http.get('languages/translations', {
      params: {
        lang,
        country_code: country,
      },
      cache: {
        ttl: 60 * 60 * 1000,
        persist: true,
        storage: 'localStorage',
      },
    }).then(({ data }) => {
      i18n.global.setLocaleMessage(locale, data)
      setStoredTranslation(locale, data)
      loadedLanguages.push(locale)
      setLoadedLanguages(loadedLanguages)
      loadLocale(locale)
      resolve(setI18nLocale(locale))
      store.dispatch('translations/togglePendingTranslations')
    }).catch(err => {
      console.error('Failed to load translations:', err)
      loadLocale(locale)
      store.dispatch('translations/togglePendingTranslations')
      resolve(setI18nLocale(locale))
    })
  })
}
